'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import React, { ReactNode, useEffect } from 'react';

import { logger } from '@/utils';

interface ClientLoggerProviderProps {
  userId?: string;
  children: ReactNode;
}

const ClientLoggerProvider = ({
  userId,
  children,
}: ClientLoggerProviderProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // Initialize logger with session context
    logger.init({
      userId,
      userAgent: navigator.userAgent,
      path: pathname,
    });

    // Log page view
    logger.debug('Page view', {
      path: pathname,
      query: Object.fromEntries(searchParams.entries()),
    });

    // Set up global error handling
    const handleError = (event: ErrorEvent) => {
      const { message, filename, lineno, colno, error } = event;

      logger.error('Uncaught error', {
        message,
        source: filename,
        line: lineno,
        column: colno,
        stack: error?.stack,
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      logger.error('Unhandled promise rejection', {
        reason: event.reason?.toString(),
        stack: event.reason?.stack,
      });
    };

    // Add global error handlers
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Clean up
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection,
      );
    };
  }, [pathname, searchParams, userId]);

  // Track navigation changes
  useEffect(() => {
    logger.debug('Navigation', {
      path: pathname,
      query: Object.fromEntries(searchParams.entries()),
    });
  }, [pathname, searchParams]);

  return <>{children}</>;
};

export { ClientLoggerProvider };
